<script setup>
import { computed, ref, onMounted, watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useUISettings } from 'dashboard/composables/useUISettings';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import ConversationCard from 'dashboard/components/widgets/conversation/ConversationCard.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';

const store = useStore();
const { t } = useI18n();
const { uiSettings, updateUISettings } = useUISettings();

const loading = ref(true);

const filterNegative = ref(false);
const filterNeutral = ref(false);
const filterPositive = ref(false);

const activeConversations = computed(() => {
  return store.getters.getAllConversations.filter(c => c.status === 'open');
});

const loadSettings = () => {
  const alerts = uiSettings.value?.ai_sentiment_alerts || [];
  filterNegative.value = alerts.includes('negative');
  filterNeutral.value = alerts.includes('neutral');
  filterPositive.value = alerts.includes('positive');
};

const saveSettings = async () => {
  const alerts = [];
  if (filterNegative.value) alerts.push('negative');
  if (filterNeutral.value) alerts.push('neutral');
  if (filterPositive.value) alerts.push('positive');

  await updateUISettings({ ai_sentiment_alerts: alerts });
};

watch([filterNegative, filterNeutral, filterPositive], () => {
  saveSettings();
});

const filteredConversations = computed(() => {
  const activeAlerts = uiSettings.value?.ai_sentiment_alerts || [];
  if (activeAlerts.length === 0) return [];

  return activeConversations.value.filter(c => {
    const sentiment = c.custom_attributes?.sentiment;
    return activeAlerts.includes(sentiment);
  });
});

const fetchConversations = async () => {
  loading.value = true;
  await store.dispatch('fetchAllConversations', {
    assigneeType: 'all',
    status: 'open',
  });
  loading.value = false;
};

onMounted(() => {
  loadSettings();
  fetchConversations();
});

const openConversation = chat => {
  const accountId = store.getters.getCurrentAccountId;
  window.location = `/app/accounts/${accountId}/conversations/${chat.id}`;
};

const getContact = chat => {
  return (
    store.getters['contacts/getContact'](chat.meta.sender.id) ||
    chat.meta.sender
  );
};
</script>

<template>
  <div class="flex flex-col w-full h-full p-6 bg-n-background overflow-y-auto">
    <div
      class="flex items-center justify-between pb-4 mb-4 border-b border-n-slate-3"
    >
      <h1 class="text-xl font-medium text-n-slate-12">
        {{ t('CAMPAIGN.AI_ALERTS.TITLE') }}
      </h1>
    </div>

    <div class="mb-6 flex gap-4">
      <span class="text-sm font-medium text-n-slate-11">{{
        t('CAMPAIGN.AI_ALERTS.MONITOR')
      }}</span>
      <label class="flex items-center gap-2 cursor-pointer">
        <Checkbox v-model="filterNegative" />
        <span class="text-sm text-n-slate-12">{{
          t('CAMPAIGN.AI_ALERTS.NEGATIVE')
        }}</span>
      </label>
      <label class="flex items-center gap-2 cursor-pointer">
        <Checkbox v-model="filterNeutral" />
        <span class="text-sm text-n-slate-12">{{
          t('CAMPAIGN.AI_ALERTS.NEUTRAL')
        }}</span>
      </label>
      <label class="flex items-center gap-2 cursor-pointer">
        <Checkbox v-model="filterPositive" />
        <span class="text-sm text-n-slate-12">{{
          t('CAMPAIGN.AI_ALERTS.POSITIVE')
        }}</span>
      </label>
    </div>

    <div v-if="loading" class="flex items-center justify-center h-64">
      <Spinner size="lg" />
    </div>

    <div
      v-else-if="filteredConversations.length === 0"
      class="flex flex-col items-center justify-center h-64 text-n-slate-11"
    >
      <fluent-icon
        icon="checkmark-circle"
        size="48"
        class="mb-4 text-n-green-10"
      />
      <p>{{ t('CAMPAIGN.AI_ALERTS.EMPTY') }}</p>
    </div>

    <div v-else class="grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
      <div
        v-for="chat in filteredConversations"
        :key="chat.id"
        class="border border-n-slate-3 rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow cursor-pointer bg-white"
        @click="openConversation(chat)"
      >
        <ConversationCard
          :chat="chat"
          :current-contact="getContact(chat)"
          :assignee="chat.meta.assignee || {}"
          :hide-thumbnail="false"
        />
      </div>
    </div>
  </div>
</template>
